import { CLIENT_MESSAGES, type InteractMessage, type ObjectEvent } from '@veriverse/shared';
import { type Room } from 'colyseus.js';
import { useEffect, useRef, useState } from 'react';
import { parseObjectData } from '../game/object-events.js';
import { useSessionStore } from '../state/session-store.js';
import { objectEvents } from './object-event-handlers.js';

/** Sends one interaction (`join`, `start`, `hit`, ...) to a station object. */
export function sendStation(room: Room, objectId: number, action: string, data?: unknown): void {
  const message: InteractMessage =
    data === undefined ? { objectId, action } : { objectId, action, data };
  room.send(CLIENT_MESSAGES.interact, message);
}

/** The station's synced game state, live; `undefined` while nobody is at the object. */
export function useStationData<T>(room: Room, objectId: number): T | undefined {
  const [data, setData] = useState<T | undefined>(undefined);
  useEffect(() => {
    const read = () => {
      const objects = (room.state as { objects?: Map<string, { data?: string }> } | undefined)
        ?.objects;
      const next = parseObjectData<T>(objects?.get(String(objectId))?.data);
      setData((previous) => (JSON.stringify(previous) === JSON.stringify(next) ? previous : next));
    };
    read();
    room.onStateChange(read);
    return () => {
      room.onStateChange.remove(read);
    };
  }, [room, objectId]);
  return data;
}

const SYNC_ROUNDS = 6;
const SYNC_GAP_MS = 350;

/**
 * Server-clock offset for timing games: the server's `time` replies to a few `sync` requests,
 * keeping the sample with the shortest round trip (offset = server - (sent + rtt/2)).
 * Returns `now()` in server time (a fallback of the coarse room clock until measured).
 */
export function useServerNow(
  room: Room,
  objectId: number,
  objectType: string,
): { now: () => number; ready: boolean } {
  const offset = useRef(0);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let best = Number.POSITIVE_INFINITY;
    let sentAt = 0;
    let rounds = 0;
    const onEvent = (event: ObjectEvent) => {
      if (event.objectId !== objectId) return;
      const rtt = Date.now() - sentAt;
      const server = (event.data as { now?: number } | undefined)?.now;
      if (typeof server !== 'number' || rtt >= best) return;
      best = rtt;
      offset.current = server - (sentAt + rtt / 2);
      setReady(true);
    };
    const remove = objectEvents.on(objectType, 'time', onEvent);
    const timer = window.setInterval(() => {
      if (rounds++ >= SYNC_ROUNDS) {
        window.clearInterval(timer);
        return;
      }
      sentAt = Date.now();
      sendStation(room, objectId, 'sync');
    }, SYNC_GAP_MS);
    return () => {
      window.clearInterval(timer);
      remove();
    };
  }, [room, objectId, objectType]);
  return { now: () => Date.now() + offset.current, ready };
}

/** Closes the panel on Escape and when the player is no longer at the station. */
export function closeGamePanel(): void {
  useSessionStore.getState().setGamePanel(undefined);
}
